import { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";

export default function GeoRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/geo-requests", { params: { status_filter: filter } });
      setItems(data);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, approve, duration) => {
    try {
      await api.post(`/admin/geo-requests/${id}/decision`, { approve, duration });
      load();
    } catch (ex) {
      alert(formatErr(ex.response?.data?.detail));
    }
  };

  const mapsLink = (lat, lng) => `https://maps.google.com/?q=${lat},${lng}`;

  return (
    <div className="p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">GEO APPROVALS</div>
          <h1 className="text-2xl font-semibold mt-1">Location requests</h1>
          <p className="text-xs text-zinc-500 mt-1">Users who tried to sign in from outside their allowed zone.</p>
        </div>
        <div className="flex gap-2 text-xs">
          {["pending", "approved", "denied", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-md border ${filter === f ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
        {loading && <div className="p-6 text-sm text-zinc-500">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-6 text-sm text-zinc-500">No {filter !== "all" ? filter + " " : ""}requests.</div>}
        {items.map((r) => (
          <div key={r.id} className="p-4 grid grid-cols-12 items-center gap-4 text-sm" data-testid={`geo-req-${r.id}`}>
            <div className="col-span-3 min-w-0">
              <div className="text-zinc-200 truncate">{r.user_email}</div>
              <div className="text-xs text-zinc-500 truncate">{r.user_name || ""}</div>
            </div>
            <div className="col-span-3">
              <a target="_blank" rel="noreferrer" href={mapsLink(r.lat, r.lng)} className="text-xs text-emerald-300 hover:underline font-mono">
                {Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)}
              </a>
              {r.accuracy_m != null && <div className="text-[11px] text-zinc-500">±{Math.round(r.accuracy_m)} m</div>}
            </div>
            <div className="col-span-3 text-zinc-400 text-xs truncate" title={r.message}>{r.message || "—"}</div>
            <div className="col-span-1 text-[11px] text-zinc-500">{new Date(r.requested_at).toLocaleString()}</div>
            <div className="col-span-2 flex justify-end gap-1">
              {r.status === "pending" ? (
                <>
                  <button data-testid={`approve-1d-${r.id}`} onClick={() => decide(r.id, true, "1day")}
                    className="text-xs px-2 py-1 rounded border border-emerald-700 text-emerald-300 hover:bg-emerald-900/30">+1 day</button>
                  <button data-testid={`approve-forever-${r.id}`} onClick={() => decide(r.id, true, "forever")}
                    className="text-xs px-2 py-1 rounded border border-emerald-700 text-emerald-300 hover:bg-emerald-900/30">Forever</button>
                  <button data-testid={`deny-${r.id}`} onClick={() => decide(r.id, false)}
                    className="text-xs px-2 py-1 rounded border border-red-900/60 text-red-400 hover:bg-red-950">Deny</button>
                </>
              ) : (
                <span className={`text-xs px-2 py-0.5 rounded ${r.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-red-900/30 text-red-300 border border-red-900/60"}`}>
                  {r.status}{r.expires_at ? ` · until ${new Date(r.expires_at).toLocaleString()}` : (r.status === "approved" ? " · forever" : "")}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
